import TagLibrary from '@components/interview/Tag/TagLibrary';
import Spinner from '@components/spinner/Spinner';
import { useDeleteStudyTag } from '@hooks/deleteQueries';
import { useFetchStudyTags, useAllStudies } from '@hooks/getQueries';
import { useAddStudyTag, useCopyTagsFromStudy } from '@hooks/postQueries';
import { Icon } from '@iconify/react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  alpha,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

const TagLibraryModal = ({ open, onClose, studyId }) => {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [newTagName, setNewTagName] = useState('');
  const [newTagValue, setNewTagValue] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedStudy, setSelectedStudy] = useState(null);

  // Fetch tags
  const {
    data: studyTags,
    isLoading,
    refetch: refetchTags,
  } = useFetchStudyTags({
    studyId,
    searchQuery,
  });

  // Fetch all studies for copy feature
  const { data: allStudiesData } = useAllStudies();
  const otherStudies = (allStudiesData || []).filter((s) => s.id !== studyId);

  // Mutations
  const { mutateAsync: addTag, isPending: isAddingTag } = useAddStudyTag();
  const { mutateAsync: deleteTag } = useDeleteStudyTag();
  const { mutateAsync: copyTags, isPending: isCopying } = useCopyTagsFromStudy();

  // Handle adding a new tag
  const handleAddTag = async () => {
    if (!newTagName.trim()) return;

    const newTag = {
      attribute: newTagName.trim(),
      values: newTagValue.trim() ? [newTagValue.trim()] : [],
    };

    try {
      await addTag({ studyId, payload: newTag });
      setNewTagName('');
      setNewTagValue('');
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };

  // Handle deleting a tag
  const handleDeleteTag = async (tagId) => {
    try {
      await deleteTag({ studyId, tagId });
      refetchTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
  };

  // Handle copying tags from another study
  const handleCopyTags = async () => {
    if (!selectedStudy) return;

    try {
      await copyTags({
        targetStudyId: studyId,
        sourceStudyId: selectedStudy.id,
      });
      setSelectedStudy(null);
      setActiveTab(0);
      refetchTags();
    } catch (error) {
      console.error('Error copying tags:', error);
    }
  };

  const tagCount = studyTags?.tags?.length || 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          height: '70vh',
          maxHeight: '600px',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          fontWeight: 600,
          py: 1.5,
          px: 2.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Icon icon="mdi:tag-multiple" width="22" height="22" />
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Tag Library
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Organize your data with custom tags
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Tabs */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            minHeight: 40,
            borderBottom: `1px solid ${theme.palette.divider}`,
            px: 2,
          }}
        >
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <span>Tags</span>
                {tagCount > 0 && (
                  <Chip label={tagCount} size="small" sx={{ height: 18, fontSize: '0.7rem' }} />
                )}
              </Box>
            }
            sx={{ minHeight: 40, py: 1 }}
          />
          <Tab
            label={
              <Box display="flex" alignItems="center" gap={0.5}>
                <ContentCopyIcon sx={{ fontSize: 16 }} />
                <span>Import</span>
              </Box>
            }
            sx={{ minHeight: 40, py: 1 }}
          />
        </Tabs>

        {activeTab === 0 ? (
          <>
            {/* Add Tag Section */}
            <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography
                variant="caption"
                fontWeight={600}
                color="text.secondary"
                sx={{ mb: 1, display: 'block' }}
              >
                CREATE NEW TAG
              </Typography>
              <Box display="flex" gap={1} alignItems="flex-start">
                <TextField
                  size="small"
                  placeholder="Tag name (e.g., Gender)"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  placeholder="First value (optional)"
                  value={newTagValue}
                  onChange={(e) => setNewTagValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddTag}
                  disabled={!newTagName.trim() || isAddingTag}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Add
                </Button>
              </Box>
            </Box>

            {/* Search */}
            {tagCount > 0 && (
              <Box sx={{ px: 2, pt: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon icon="mdi:magnify" width={18} height={18} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            )}

            {/* Tags List */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
              {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                  <Spinner />
                </Box>
              ) : tagCount > 0 ? (
                <TagLibrary tags={studyTags.tags} onDeleteTag={handleDeleteTag} studyId={studyId} />
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                  color="text.secondary"
                >
                  <Icon
                    icon="mdi:tag-off"
                    width={40}
                    height={40}
                    style={{ opacity: 0.4, marginBottom: 12 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    No tags yet
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Create your first tag above or import from another study
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        ) : (
          /* Import Tab */
          <Box sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import tags from another study. Existing tags will be preserved and new tags will be
              added.
            </Typography>

            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ mb: 1 }}>
              SELECT SOURCE STUDY
            </Typography>

            <Autocomplete
              options={otherStudies}
              getOptionLabel={(option) => option.title || option.name || ''}
              value={selectedStudy}
              onChange={(_, newValue) => setSelectedStudy(newValue)}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Search for a study..." />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>
                      {option.title || option.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.type === 'Interview' ? 'Interview' : 'Survey'} •{' '}
                      {option.total_responses || 0} responses
                    </Typography>
                  </Box>
                </Box>
              )}
              sx={{ mb: 2 }}
            />

            {selectedStudy && (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  mb: 2,
                }}
              >
                <Typography variant="body2" fontWeight={500}>
                  {selectedStudy.title || selectedStudy.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tags from this study will be merged with your current tags
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 'auto' }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyTags}
                disabled={!selectedStudy || isCopying}
              >
                {isCopying ? 'Importing...' : 'Import Tags'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TagLibraryModal;
